import React, {
  createContext,
  Fragment,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Button from '#components/controls/button/Button';
import Typography from '#components/typography/Typography';
import useTranslation from 'next-translate/useTranslation';
import DialogHeader from '#components/dialog/DialogHeader';
import DialogPanel from '#components/dialog/DialogPanel';
import { signIn } from 'next-auth/react';

type RequiredTranslationKeys = {
  title: string;
  description: string;
};

type Props = {
  translations?: RequiredTranslationKeys;
  open: boolean;
  onClose: () => void;
};

const translationKeys = {
  purchase: {
    title: 'purchaseLoginModalTitle',
    description: 'purchaseLoginModalDescription',
  },
  playback: {
    title: 'freeMediaLoginModalTitle',
    description: 'freeMediaLoginModalDescription',
  },
};

const LoginNotificationDialogContext = createContext(null);

export const LoginNotificationDialogProvider = ({ children }) => {
  const [translations, setTranslations] = useState<
    RequiredTranslationKeys | undefined
  >();
  const [open, setOpen] = useState(false);
  const onCloseRef = useRef<(() => void) | undefined>();

  const handleDialogClose = () => {
    setOpen(false);
    onCloseRef.current?.();
  };

  const showLoginNotificationDialog = useCallback(
    (
      intendedAction: 'purchase' | 'playback' = 'playback',
      onClose?: () => void
    ) => {
      setTranslations(translationKeys[intendedAction]);
      setOpen(true);
      onCloseRef.current = onClose;
    },
    []
  );

  return (
    <LoginNotificationDialogContext.Provider
      value={{
        open,
        showLoginNotificationDialog,
      }}
    >
      {children}
      <DialogLoginNotification
        translations={translations}
        open={open}
        onClose={handleDialogClose}
      />
    </LoginNotificationDialogContext.Provider>
  );
};

export const useLoginNotificationDialog = () =>
  React.useContext(LoginNotificationDialogContext);

function DialogLoginNotification({
  open,
  onClose,
  translations = {
    title: 'freeMediaLoginModalTitle',
    description: 'freeMediaLoginModalDescription',
  },
}: Props) {
  const { t } = useTranslation();

  return (
    <Transition
      as={Fragment}
      show={open}
      enter="tw-transition-all tw-ease-out tw-duration-200"
      enterFrom="tw-opacity-0"
      enterTo="tw-opacity-100"
      leave="tw-transition-all tw-duration-200"
      leaveFrom="tw-opacity-100"
      leaveTo="tw-opacity-0"
    >
      <Dialog
        onClose={onClose}
        className="tw-fixed tw-z-100 tw-inset-0 tw-overflow-y-auto"
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-min-h-screen">
          <div className="tw-fixed tw-inset-0 tw-bg-black tw-opacity-70" />

          <DialogPanel>
            <DialogHeader title={t(translations.title)} onClose={onClose} />
            <Typography
              classes="tw-text-center tw-mb-16"
              dangerouslySetInnerHTML={{
                __html: t(translations.description),
              }}
            />
            <div className="tw-flex">
              <Button title={t('login')} onClick={() => signIn('cidaas')} />
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </Transition>
  );
}

export default DialogLoginNotification;
